import { Alert } from '@@/Alert';
import { Link } from '@@/Link';
import { LinkButton } from '@@/LinkButton';

import { PolicyId } from '../../../types';
import { usePolicy } from '../../../queries/usePolicy';
import { useCanManagePolicies } from '../../../utils/useCanManagePolicies';

import { USER_SCOPE_OPTIONS } from './types';
import { useAutomaticNamespaceProvisioningInitialValues } from './useAutomaticNamespaceProvisioningInitialValues';

interface Props {
  policyId: PolicyId;
  policyName: string;
}

/**
 * Explains, on a page that edits environment access, that users in the ANP
 * policy's scope get their access from the policy instead.
 */
export function AutomaticNamespaceProvisioningAccessAlert({
  policyId,
  policyName,
}: Props) {
  const canManagePolicies = useCanManagePolicies();
  const policyQuery = usePolicy(policyId);
  const { userScopes } = useAutomaticNamespaceProvisioningInitialValues(
    policyQuery.data ?? undefined
  );
  const scopeLabels = USER_SCOPE_OPTIONS.filter((option) =>
    userScopes.includes(option.value)
  ).map((option) => option.label);
  const affectedUsers =
    scopeLabels.length > 0
      ? `Users whose last login used ${scopeLabels.join(' or ')}`
      : 'Users in its scope';

  return (
    <Alert color="info" title="Automatic Namespace Provisioning policy applies">
      <div className="inline-flex flex-wrap items-center gap-2">
        <span>
          The policy{' '}
          {canManagePolicies ? (
            <Link
              to="portainer.policy"
              params={{ id: policyId, tab: 'config' }}
              data-cy="anp-policy-name-link"
              className="text-inherit underline hover:text-inherit"
            >
              {policyName}
            </Link>
          ) : (
            <strong>&apos;{policyName}&apos;</strong>
          )}{' '}
          applies to this environment. {affectedUsers} are eligible for a
          personal Kubernetes namespace with the role configured in this policy.
          Their effective access may also include permissions granted through
          other access configurations.
        </span>
        {canManagePolicies && (
          <LinkButton
            to="portainer.policy"
            params={{ id: policyId, tab: 'config' }}
            data-cy="anp-view-policy-link"
            className="h-fit w-fit"
            size="small"
            color="light"
          >
            Manage &apos;{policyName}&apos;
          </LinkButton>
        )}
      </div>
    </Alert>
  );
}
